import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  timelineOppositeContentClasses,
  TimelineSeparator,
} from "@mui/lab";
import { Grid2 as Grid } from "@mui/material";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useFormat } from "@sinch/core/hooks/format";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { RatelistWithRevisions } from "@sinch/views/RateBuilder/components/RatelistStatus/queries.graphql";
import {
  RatelistWithRevisionsQuery,
  RatelistWithRevisionsQueryVariables,
} from "@sinch/views/RateBuilder/components/RatelistStatus/queries.types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useRateRevision } from "@sinch/components/application/Rate/tools";
import { LoadingWrapper } from "@sinch/components/LoadingWrapper/LoadingWrapper";

export const Revisions = ({
  ratelistId,
  onClickDetail,
}: {
  ratelistId: string;
  onClickDetail: (ratelistId: string) => void;
}) => {
  const { t } = useTranslation(["common", "draft"]);
  const { date, time, name: workerName } = useFormat();
  const { getRevisionNumber } = useRateRevision();

  const { data, loading } = useQueryHandler<RatelistWithRevisionsQuery, RatelistWithRevisionsQueryVariables>(
    RatelistWithRevisions,
    { ratelistId: ratelistId as string },
    { skip: !ratelistId }
  );

  return (
    <LoadingWrapper on={loading} sx={{ py: 2, px: 4 }}>
      <Timeline
        sx={{
          [`& .${timelineOppositeContentClasses.root}`]: {
            flex: 0.2,
            whiteSpace: "nowrap",
          },
        }}
      >
        {data?.ratelist?.lineageRatelists.map(({ id, modified, modifier, name }, ix) => (
          <TimelineItem key={id}>
            <TimelineOppositeContent
              color="textSecondary"
              sx={{ paddingTop: "12px", minWidth: "150px", textAlign: "left" }}
            >
              {date.medium(modified)} {time.short(modified)}
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot
                color={id === ratelistId ? "primary" : "secondary"}
                sx={{
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {getRevisionNumber(ix + 1)}
              </TimelineDot>
              {ix !== data!.ratelist!.lineageRatelists.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <Grid
                component="a"
                container
                onClick={() => {
                  onClickDetail(id);
                }}
                sx={({ palette }) => ({
                  "&, &:hover": {
                    color: palette.text.primary,
                  },
                })}
              >
                <Grid>
                  <Stack>
                    <Typography
                      sx={({ typography }) => ({
                        fontWeight: id === ratelistId ? typography.fontWeightMedium : undefined,
                      })}
                      variant="caption"
                    >
                      {name}
                    </Typography>
                    {modifier && (
                      <Typography variant="descriptionSmall">
                        {t("modifier")}: {workerName.full(modifier)}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
                <Grid></Grid>
              </Grid>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </LoadingWrapper>
  );
};
